import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mail, Plus, Trash2, RefreshCw, Check, ShieldCheck, AlertCircle, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  display_name: '',
  email_address: '',
  password: '',
  provider: 'titan',
  smtp_host: '',
  smtp_port: '',
  imap_host: '',
  imap_port: '',
  username: '',
  signature: '',
  is_active: true
};

export const MailboxManager = ({ getAuthToken, onMailboxesChanged }) => {
  const [mailboxes, setMailboxes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tested, setTested] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const authConfig = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

  const load = async () => {
    setLoading(true);
    try {
      const [boxRes, provRes] = await Promise.all([
        axios.get(`${API}/api/mailboxes`, authConfig()),
        axios.get(`${API}/api/mailboxes/providers`, authConfig())
      ]);
      setMailboxes(boxRes.data || []);
      setProviders(provRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load mailboxes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const preset = providers.find(p => p.id === form.provider);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setTested(false);
    setDialogOpen(true);
  };

  const openEdit = (mailbox) => {
    setEditingId(mailbox.id);
    setForm({
      ...emptyForm,
      display_name: mailbox.display_name || '',
      email_address: mailbox.email_address || '',
      provider: mailbox.provider || 'titan',
      smtp_host: mailbox.smtp_host || '',
      smtp_port: mailbox.smtp_port || '',
      imap_host: mailbox.imap_host || '',
      imap_port: mailbox.imap_port || '',
      username: mailbox.username || '',
      signature: mailbox.signature || '',
      is_active: mailbox.is_active !== false
    });
    setTested(false);
    setDialogOpen(true);
  };

  const payload = () => ({
    display_name: form.display_name || form.email_address,
    email_address: form.email_address.trim(),
    password: form.password,
    provider: form.provider,
    smtp_host: form.smtp_host || undefined,
    smtp_port: form.smtp_port ? parseInt(form.smtp_port, 10) : undefined,
    imap_host: form.imap_host || undefined,
    imap_port: form.imap_port ? parseInt(form.imap_port, 10) : undefined,
    username: form.username || undefined,
    signature: form.signature,
    is_active: form.is_active
  });

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await axios.post(`${API}/api/mailboxes/test`, payload(), authConfig());
      setTested(true);
      setTestResult({ ok: true, message: 'SMTP and IMAP connection successful' });
      toast.success('SMTP and IMAP connection successful');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Connection failed';
      setTested(false);
      setTestResult({ ok: false, message });
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        const body = payload();
        if (!form.password) delete body.password;
        delete body.email_address;
        delete body.provider;
        await axios.patch(`${API}/api/mailboxes/${editingId}`, body, authConfig());
        toast.success('Mailbox updated');
      } else {
        await axios.post(`${API}/api/mailboxes`, payload(), authConfig());
        toast.success('Mailbox connected');
      }
      setDialogOpen(false);
      await load();
      onMailboxesChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save mailbox');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mailbox) => {
    if (!window.confirm(`Disconnect ${mailbox.email_address}? Synced messages will be removed from the CRM (the mailbox itself is untouched).`)) return;
    try {
      await axios.delete(`${API}/api/mailboxes/${mailbox.id}`, authConfig());
      toast.success('Mailbox disconnected');
      await load();
      onMailboxesChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not disconnect mailbox');
    }
  };

  const handleSync = async (mailbox) => {
    try {
      const res = await axios.post(`${API}/api/mailboxes/${mailbox.id}/sync`, {}, authConfig());
      toast.success(`Synced ${res.data.synced} messages`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    }
  };

  return (
    <div className="p-5 space-y-5" data-testid="mailbox-manager">
      <div className="flex items-start justify-between border-b border-slate-100 pb-4 gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Connected Mailboxes</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Connect professional mailboxes (Titan, GoDaddy, Zoho, Google Workspace) to send and receive from your own address.
            The default no-reply sender keeps working exactly as before.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="connect-mailbox-btn">
          <Plus className="h-4 w-4 mr-2" /> Connect Mailbox
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-8">
          <RefreshCw className="h-4 w-4 animate-spin" /> Loading mailboxes...
        </div>
      ) : mailboxes.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
          <Mail className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No mailboxes connected yet.</p>
          <p className="text-xs text-slate-400 mt-1">
            For Titan: enable "Titan on other apps" in webmail settings, then use your mailbox or app password.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {mailboxes.map(mailbox => (
            <div
              key={mailbox.id}
              className="border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              data-testid={`mailbox-row-${mailbox.email_address}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800 text-sm">{mailbox.display_name}</span>
                  <Badge className="bg-slate-100 text-slate-600 text-[10px] rounded-md">{mailbox.email_address}</Badge>
                  {mailbox.is_active
                    ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px] rounded-md">Active</Badge>
                    : <Badge className="bg-slate-200 text-slate-600 text-[10px] rounded-md">Paused</Badge>}
                  {mailbox.unread_count > 0 && (
                    <Badge className="bg-blue-100 text-blue-700 text-[10px] rounded-md">{mailbox.unread_count} unread</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  SMTP {mailbox.smtp_host}:{mailbox.smtp_port} · IMAP {mailbox.imap_host}:{mailbox.imap_port}
                </p>
                <p className="text-xs mt-0.5">
                  {mailbox.last_sync_error ? (
                    <span className="text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> {mailbox.last_sync_error}
                    </span>
                  ) : (
                    <span className="text-slate-400">
                      {mailbox.last_synced_at ? `Last synced ${new Date(mailbox.last_synced_at).toLocaleString()}` : 'Not synced yet'}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => handleSync(mailbox)} data-testid={`sync-mailbox-${mailbox.id}`}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Sync
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(mailbox)} data-testid={`edit-mailbox-${mailbox.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(mailbox)} data-testid={`delete-mailbox-${mailbox.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="mailbox-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Mailbox' : 'Connect a Mailbox'}</DialogTitle>
            <DialogDescription>
              Credentials are encrypted before they are stored and are never shown again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {!editingId && (
              <div>
                <Label className="text-sm">Provider</Label>
                <Select value={form.provider} onValueChange={(v) => { setForm(prev => ({ ...prev, provider: v, smtp_host: '', smtp_port: '', imap_host: '', imap_port: '' })); setTested(false); }}>
                  <SelectTrigger className="mt-1" data-testid="mailbox-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {providers.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {preset && (
                  <p className="text-xs text-slate-400 mt-1">
                    Defaults: SMTP {preset.smtp_host}:{preset.smtp_port} · IMAP {preset.imap_host}:{preset.imap_port}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Display Name</Label>
                <Input
                  placeholder="Officium Careers"
                  value={form.display_name}
                  onChange={(e) => setForm(prev => ({ ...prev, display_name: e.target.value }))}
                  className="mt-1"
                  data-testid="mailbox-display-name"
                />
              </div>
              <div>
                <Label className="text-sm">Email Address *</Label>
                <Input
                  placeholder="careers@officiumconnect.in"
                  value={form.email_address}
                  disabled={!!editingId}
                  onChange={(e) => { setForm(prev => ({ ...prev, email_address: e.target.value })); setTested(false); }}
                  className="mt-1"
                  data-testid="mailbox-email-address"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm">{editingId ? 'Password (leave blank to keep current)' : 'Mailbox / App Password *'}</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => { setForm(prev => ({ ...prev, password: e.target.value })); setTested(false); }}
                className="mt-1"
                data-testid="mailbox-password"
              />
            </div>

            <details className="border border-slate-200 rounded-lg p-3">
              <summary className="text-sm font-medium text-slate-700 cursor-pointer">Advanced server settings</summary>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs">SMTP Host</Label>
                  <Input value={form.smtp_host} placeholder={preset?.smtp_host} onChange={(e) => setForm(prev => ({ ...prev, smtp_host: e.target.value }))} className="mt-1" data-testid="mailbox-smtp-host" />
                </div>
                <div>
                  <Label className="text-xs">SMTP Port</Label>
                  <Input value={form.smtp_port} placeholder={String(preset?.smtp_port || 465)} onChange={(e) => setForm(prev => ({ ...prev, smtp_port: e.target.value }))} className="mt-1" data-testid="mailbox-smtp-port" />
                </div>
                <div>
                  <Label className="text-xs">IMAP Host</Label>
                  <Input value={form.imap_host} placeholder={preset?.imap_host} onChange={(e) => setForm(prev => ({ ...prev, imap_host: e.target.value }))} className="mt-1" data-testid="mailbox-imap-host" />
                </div>
                <div>
                  <Label className="text-xs">IMAP Port</Label>
                  <Input value={form.imap_port} placeholder={String(preset?.imap_port || 993)} onChange={(e) => setForm(prev => ({ ...prev, imap_port: e.target.value }))} className="mt-1" data-testid="mailbox-imap-port" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Username (defaults to the email address)</Label>
                  <Input value={form.username} onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))} className="mt-1" data-testid="mailbox-username" />
                </div>
              </div>
            </details>

            <div>
              <Label className="text-sm">Signature (HTML allowed)</Label>
              <Textarea
                rows={3}
                placeholder="Best regards,<br/>Officium Connect Talent Team"
                value={form.signature}
                onChange={(e) => setForm(prev => ({ ...prev, signature: e.target.value }))}
                className="mt-1 font-mono text-xs"
                data-testid="mailbox-signature"
              />
            </div>

            <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
              <div>
                <Label className="text-sm">Active</Label>
                <p className="text-xs text-slate-400">Inactive mailboxes are hidden from the From dropdown and are not synced</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))} data-testid="mailbox-active-switch" />
            </div>
          </div>

          {testResult && (
            <div
              className={`text-xs rounded-lg px-3 py-2 border ${testResult.ok
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-red-50 border-red-200 text-red-700'}`}
              data-testid="mailbox-test-result"
            >
              {testResult.message}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleTest} disabled={testing || !form.email_address || !form.password} data-testid="test-mailbox-btn">
              {testing ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Testing...</> : <><ShieldCheck className="h-4 w-4 mr-2" />Test Connection</>}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.email_address || (!editingId && !form.password)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-mailbox-btn"
            >
              {saving ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Check className="h-4 w-4 mr-2" />{tested ? 'Save Verified Mailbox' : 'Save'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MailboxManager;
